import React from 'react';
import {observer} from 'mobx-react';
import {Box, Divider, Stack} from '@mui/material';
import {grey} from '@mui/material/colors';
import {DashboardListHeader} from 'app/components/Dashboard/DashboardListHeader';
import {MeetingsHeader} from 'app/components/MeetingsManager/MeetingsHeader/MeetingsHeader';
import {useSearch} from 'app/components/DeviceDetails/hooks/useSearch';
import {MeetingsList} from 'app/components/MeetingsManager/MeetingsList/MeetingsList';
import {MeetingsWelcome} from 'app/components/MeetingsManager/MeetingsWelcome/MeetingsWelcome';
import {useMeetings} from 'app/components/MeetingsManager/hooks/useMeetings';
import {IndeterminateProgressBar} from 'app/components/sharedReactComponents/IndeterminateProgressBar';
import {ReadyToConnect} from 'app/components/MeetingsManager/ReadyToConnect/ReadyToConnect';
import {useFilter} from 'app/components/MeetingsManager/hooks/useFilter';
import {useTeamTenants} from 'app/hooks/useTenantsQuery';
import {useTeamsConnectionUrlHandlers} from 'app/hooks/Meeting/useTeamsConnectionUrlHandlers';
import {ConnectManager} from 'app/components/MeetingsManager/ConnectManager';
import {router} from 'app/router/main';
import {MoreDetails} from 'app/components/MeetingsManager/MoreDetails/MoreDetails';
import {useTeamBilling} from 'app/components/BillingManager/hooks/useTeamBilling';
import {SecureMessage} from 'app/components/MeetingsManager/SecureMessage/SecureMessage';
import {useConnectIntegration} from 'app/components/ConnectPlatformsIntegration/ConnectPlatformsIntegration';
import {useZoomAccounts} from 'app/hooks/useZoomAccounts';
import {useCloud} from 'app/components/features/cloud';
import {FeatureLockNotice} from 'app/components/entities/billing/base';
import {ConnectGuide} from 'app/components/widgets/connect';
import {StyleSx, Sx} from 'app/types/common';
import {packSx} from 'app/util/packSx/packSx';

interface ContentProps {
  children: React.ReactNode;
  withMessage: boolean;
}

function Content({withMessage, children}: ContentProps) {
  return (
    <Stack className="meetings-manager__content">
      <Stack flexGrow={1} className="container">
        {children}

        {withMessage && <SecureMessage sx={{mt: 'auto', mb: 2}} />}
      </Stack>
    </Stack>
  );
}

interface Props {
  error?: string;
  state?: string;
}

function MeetingsManagerComponent({error, state}: Props) {
  const {team, user} = useCloud();
  const teamId = team.id;

  const canSubscribe = user.role.canEditBilling();

  const {enabled} = team.caps.connect;
  const canManageAccounts = user.role.canPairConnectAuth();

  const {search, setSearch, clearSearch} = useSearch('');

  const teamsQuery = useTeamTenants({enabled, teamId});
  const zoomQuery = useZoomAccounts({enabled, teamId});
  const tenants = teamsQuery.data ?? [];
  const accounts = zoomQuery.data ?? [];

  const {items, callsQuery, meetingsQuery} = useMeetings({enabled});

  const filteredItems = useFilter(items, search);

  const {connect, actions} = useTeamBilling();

  const teamName = team.name;
  const userId = user.id;

  useTeamsConnectionUrlHandlers({
    error,
    state,
    teamName,
  });

  const {startTeams} = useConnectIntegration();

  const handlePair = () => {
    const backUrl = `/${teamId}${router.url('meetings')}`;
    startTeams(backUrl);
  };

  if (
    callsQuery.isInitialLoading ||
    meetingsQuery.isInitialLoading ||
    teamsQuery.isInitialLoading ||
    zoomQuery.isInitialLoading
  ) {
    return (
      <>
        <DashboardListHeader
          showSearch={enabled}
          filterValue={search}
          buttonsComponent={null}
          onFilter={setSearch}
        />
        <IndeterminateProgressBar />
      </>
    );
  }

  const hasEntities = items.length > 0;
  const hasFilter = search.length > 0;

  const allowCreate = team.caps.startConnect.enabled;

  const renderBody = () => {
    if (enabled) {
      return (
        <>
          <DashboardListHeader
            showSearch={enabled}
            filterValue={search}
            buttonsComponent={null}
            onFilter={setSearch}
          />

          <Content withMessage={true}>
            <ConnectGuide
              sx={{my: 2}}
              trialSeconds={connect?.type === 'token' ? connect.balance.amount : undefined}
              billingAccess={canSubscribe}
            />

            <MeetingsHeader
              sx={{mt: 3, mb: 2}}
              accounts={accounts}
              tenants={tenants}
              canEditTenants={canManageAccounts}
              canCreate={allowCreate}
            />

            <Divider sx={{borderWidth: 2, borderColor: grey[100], mb: 5}} />

            {hasEntities ? (
              <MeetingsList
                className="meetings-manager__list"
                items={filteredItems}
                hasFilter={hasFilter}
                canStart={allowCreate}
                onClearFilter={clearSearch}
              />
            ) : (
              <>
                <ReadyToConnect />
                <MoreDetails sx={{my: 2}} />
              </>
            )}
          </Content>
        </>
      );
    }

    if (team.caps.connect.reason === 'subscription_is_dq') {
      return (
        <Content withMessage={false}>
          <LockScreen sx={{mt: 3}} paymentAccess={user.role.canEditBilling()} />
        </Content>
      );
    }

    return (
      <Content withMessage={true}>
        <MeetingsWelcome
          sx={{mt: 2}}
          canSubscribe={canSubscribe}
          onManage={() => actions.manageConnect()}
        />
      </Content>
    );
  };

  return (
    <ConnectManager
      teamId={teamId}
      userId={userId}
      canEdit={canManageAccounts}
      hasTenants={tenants.length > 0}
      reloadHosted={callsQuery.refetch}
      reloadMeetings={meetingsQuery.refetch}
      onPair={handlePair}
    >
      <div className="meetings-manager">{renderBody()}</div>
    </ConnectManager>
  );
}

export const MeetingsManager = observer(MeetingsManagerComponent);

const bgSx: StyleSx = {
  backgroundImage:
    'image-set(url(/assets/img/background/connect/bg.png) 1x, url(/assets/img/background/connect/bg@2x.png) 2x), linear-gradient(250deg, #ADB5BD 8%, #FFFFFF 110%)',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 0px, center',
  backgroundSize: 'contain',
};

interface LockProps extends Sx {
  paymentAccess: boolean;
}

function LockScreen({sx, paymentAccess}: LockProps) {
  return (
    <FeatureLockNotice
      sx={packSx(bgSx, sx)}
      logo={
        <Box
          width={170}
          component="img"
          src="/assets/img/logo/connect-logo-black.png"
          srcSet="/assets/img/logo/connect-logo-black@2x.png 2x"
          alt="Epiphan Connect logo"
        />
      }
      title="Epiphan Connect disabled"
      description="The Epiphan Connect page is currently disabled due to an unsuccessful payment. Please update your payment method to restore access."
      paymentAccess={paymentAccess}
    />
  );
}
